import { useCallback, useEffect, useState } from 'react'
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  type Node,
  type Edge,
  useNodesState,
  useEdgesState,
  ConnectionLineType,
  Panel,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import dagre from 'dagre'
import { Card } from '@/components/ui/card'
import type { NetworkData } from '@/lib/api/modules/referrals'
import { CustomNetworkNode } from './custom-network-node'
import { Loader2 } from 'lucide-react'

const nodeTypes = {
  custom: CustomNetworkNode,
}

type NetworkGraphProps = {
  data?: NetworkData
  isLoading: boolean
}

// Layout algorithm using Dagre
function getLayoutedElements(nodes: Node[], edges: Edge[], direction = 'TB') {
  const dagreGraph = new dagre.graphlib.Graph()
  dagreGraph.setDefaultEdgeLabel(() => ({}))

  const nodeWidth = 200
  const nodeHeight = 80

  dagreGraph.setGraph({ rankdir: direction, ranksep: 250, nodesep: 250 })

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight })
  })

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target)
  })

  dagre.layout(dagreGraph)

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id)
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeWithPosition.y - nodeHeight / 2,
      },
    }
  })

  return { nodes: layoutedNodes, edges }
}

// Color scale based on referral performance
function getNodeColor(referralCount: number): string {
  if (referralCount === 0) return 'hsl(220, 70%, 90%)' // Light blue
  if (referralCount <= 2) return 'hsl(220, 90%, 60%)' // Blue
  if (referralCount <= 5) return 'hsl(270, 80%, 60%)' // Purple
  if (referralCount <= 10) return 'hsl(300, 85%, 60%)' // Pink
  return 'hsl(330, 90%, 65%)' // Hot pink
}

export function NetworkGraph({ data, isLoading }: NetworkGraphProps) {
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([])
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([])
  const [selectedNode, setSelectedNode] = useState<string | null>(null)

  // Convert API data to React Flow format
  useEffect(() => {
    if (!data) return

    const flowNodes: Node[] = data.nodes.map((node) => ({
      id: node.id,
      type: 'custom',
      position: { x: 0, y: 0 }, // Will be set by layout
      data: {
        name: node.name,
        email: node.email,
        referralCount: node.referralCount,
        totalEarnings: node.totalEarnings,
        status: node.status,
        color: getNodeColor(node.referralCount),
      },
    }))

    const flowEdges: Edge[] = data.edges.map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      type: ConnectionLineType.SmoothStep,
      animated: false,
      style: {
        stroke: '#94a3b8',
        strokeWidth: 2,
      },
      markerEnd: {
        type: 'arrowclosed' as const,
        color: '#94a3b8',
      },
      data: {
        orderAmount: edge.orderAmount,
      },
    }))

    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(flowNodes, flowEdges)

    setNodes(layoutedNodes)
    setEdges(layoutedEdges)
  }, [data, setNodes, setEdges])

  // Handle node click - highlight connections
  const onNodeClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      if (selectedNode === node.id) {
        setSelectedNode(null)
        // Reset all nodes and edges to default
        setNodes((nds) =>
          nds.map((n) => ({
            ...n,
            style: { ...n.style, opacity: 1 },
          }))
        )
        setEdges((eds) =>
          eds.map((e) => ({
            ...e,
            style: { ...e.style, opacity: 1, strokeWidth: 2 },
          }))
        )
      } else {
        setSelectedNode(node.id)
        // Find connected nodes
        const connectedNodeIds = new Set<string>([node.id])
        const connectedEdgeIds = new Set<string>()

        edges.forEach((edge) => {
          if (edge.source === node.id || edge.target === node.id) {
            connectedNodeIds.add(edge.source)
            connectedNodeIds.add(edge.target)
            connectedEdgeIds.add(edge.id)
          }
        })

        // Dim non-connected nodes and edges
        setNodes((nds) =>
          nds.map((n) => ({
            ...n,
            style: {
              ...n.style,
              opacity: connectedNodeIds.has(n.id) ? 1 : 0.2,
            },
          }))
        )
        setEdges((eds) =>
          eds.map((e) => ({
            ...e,
            style: {
              ...e.style,
              opacity: connectedEdgeIds.has(e.id) ? 1 : 0.1,
              strokeWidth: connectedEdgeIds.has(e.id) ? 3 : 2,
            },
          }))
        )
      }
    },
    [selectedNode, edges, setNodes, setEdges]
  )

  if (isLoading) {
    return (
      <Card className="h-[600px] flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Carregando rede de indicações...</p>
        </div>
      </Card>
    )
  }

  if (!data || data.nodes.length === 0) {
    return (
      <Card className="h-[600px] flex items-center justify-center">
        <div className="text-center space-y-2">
          <p className="text-muted-foreground">Nenhum membro encontrado</p>
          <p className="text-sm text-muted-foreground">Ajuste os filtros ou aguarde mais dados</p>
        </div>
      </Card>
    )
  }

  return (
    <Card className="h-[700px] p-0 overflow-hidden">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        nodeTypes={nodeTypes}
        fitView
        minZoom={0.1}
        maxZoom={2}
        defaultEdgeOptions={{
          type: ConnectionLineType.SmoothStep,
        }}
      >
        <Background color="#94a3b8" gap={16} />
        <Controls />
        <MiniMap
          nodeColor={(node) => (node.data?.color as string) || '#cbd5e1'}
          maskColor="rgba(0, 0, 0, 0.1)"
        />
        <Panel
          position="top-right"
          className="bg-background/95 backdrop-blur p-4 rounded-lg border shadow-lg space-y-3"
        >
          <div className="text-sm font-semibold">Legenda</div>
          <div className="space-y-2 text-xs">
            <div className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: 'hsl(220, 90%, 60%)' }}
              />
              <span>0-2 indicações</span>
            </div>
            <div className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: 'hsl(270, 80%, 60%)' }}
              />
              <span>3-5 indicações</span>
            </div>
            <div className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: 'hsl(300, 85%, 60%)' }}
              />
              <span>6-10 indicações</span>
            </div>
            <div className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: 'hsl(330, 90%, 65%)' }}
              />
              <span>11+ indicações</span>
            </div>
          </div>
          <div className="pt-2 border-t text-xs text-muted-foreground">
            Clique em um nó para destacar conexões
          </div>
        </Panel>
      </ReactFlow>
    </Card>
  )
}
